import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  addContractor,
  getContractors,
  updateContractor,
} from "../../../actions/contractor";
import { Button } from "../../../components/library/button";
import { Content } from "../../../components/library/content";
import {
  CheckCircleIcon,
  PlusIcon,
  WarningIcon,
} from "../../../components/library/icon";
import { Radio } from "../../../components/library/radio";
import { RadioGroup } from "../../../components/library/radio/radio-group";
import { TextInput } from "../../../components/library/text-input";
import { useForm } from "../../../hooks/use-form";
import { useId } from "../../../hooks/use-id";
import dispatch from "../../../middleware";
import { ContractorAddEditForm, ContractorDetails } from "../../../models";
import {
  getErrorMsgFromResponse,
  isEmail,
  isMobile,
  isName,
} from "../../../utils";

interface AddContractorFormProps {
  contractorId?: string;
  onContractorAdded: () => void;
  editValues?: ContractorDetails | null;
}

function getDefaultFormState(
  contractorData?: ContractorDetails | null
): ContractorAddEditForm {
  return {
    fullName: contractorData?.fullName ?? "",
    mobile: contractorData?.mobile ?? "",
    email: contractorData?.email ?? "",
    isTransactionAllowed: contractorData?.isTransactionAllowed ?? true,
    contractorId: contractorData?.employersContractorId ?? "",
  };
}

function ContentHeader({ isEditForm }: { isEditForm: boolean }) {
  return (
    <p className="primary-color fw-600 m-0">
      {isEditForm ? "Edit Contractor" : "Add Contractor"}
    </p>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <Button size="lg" variant="secondary" color="brand" onClick={onClose}>
      Close
    </Button>
  );
}

export default function AddContractorForm({
  onContractorAdded,
  editValues,
  contractorId,
}: AddContractorFormProps) {
  const formId = useId();
  const storeDispatch = useDispatch();
  const isEditForm = !!editValues;

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldValidationStatus, setFieldValidationStatus] = useState<
    Partial<Record<"email" | "mobile", boolean>>
  >({});

  const form = useForm<ContractorAddEditForm>({
    initialValues: getDefaultFormState(editValues),
    validationRules: {
      fullName: (value) => {
        if (!value || !isName(value)) return "Please enter a valid Name";
        return null;
      },
      email: (value) => {
        if (!value) return null;
        if (!isEmail(value)) return "Please enter a valid Email Id";
      },
      mobile: (value) => {
        if (!value) return null;
        if (!isMobile(value)) return "Please enter a valid Mobile Number";
      },
    },
  });

  async function handleFormSubmit(formData: ContractorAddEditForm) {
    if (!formData.email && !formData.mobile) {
      form.setFieldError("email", "Please enter either Email or Mobile");
      form.setFieldError("mobile", "Please enter either Email or Mobile");
      return;
    }

    const payload = { ...formData } as ContractorAddEditForm;
    if (!formData.email) delete payload.email;
    let message: string;
    try {
      if (isEditForm) {
        if (!contractorId)
          throw new Error("Not able to update, Contractor ID missing");
        await dispatch(
          storeDispatch,
          updateContractor(formData.isTransactionAllowed, contractorId)
        );
        message = `Details of ${payload.fullName}, Contractor Id: ${payload.contractorId} has been updated successfully`;
      } else {
        const resp: ContractorDetails | Record<string, string> = await dispatch(
          storeDispatch,
          addContractor(payload)
        );

        message = "Contractor added successfully";
      }
      await dispatch(storeDispatch, getContractors({ pageNo: 1, limit: 10 }));
      setSuccessMessage(message);
    } catch (error) {
      setErrorMessage(getErrorMsgFromResponse(error, error?.message));
    }
  }
  if (successMessage || errorMessage) {
    return (
      <FormFeedback
        isEditForm={isEditForm}
        successMessage={successMessage}
        errorMessage={errorMessage}
        onAddAnother={() => {
          form.reset();
          setFieldValidationStatus({});
          setSuccessMessage("");
          setErrorMessage("");
        }}
        onTryAgain={() => setErrorMessage("")}
        onClose={onContractorAdded}
      />
    );
  }

  return (
    <Content
      header={() => <ContentHeader isEditForm={isEditForm} />}
      footer={
        <div className="d-flex alight-items-center justify-content-end">
          <Button
            size="lg"
            leftSection={<PlusIcon size="md" />}
            disabled={!form.isValid || form.isAnyFieldLoading}
            form={formId}
          >
            {isEditForm ? "Update" : "Add"} Contractor
          </Button>
        </div>
      }
      fixedHeaderFooter
    >
      <form
        id={formId}
        onSubmit={form.onSubmit(handleFormSubmit)}
        noValidate
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          columnGap: "24px",
          rowGap: "20px",
        }}
      >
        <TextInput
          label="Full Name"
          required
          autoFocus={!isEditForm}
          disabled={Boolean(contractorId)}
          {...form.getInputProps("fullName")}
          hasClearButton
          onClear={() => form.setFieldValue("fullName", "")}
        />
        <TextInput
          label="Email ID"
          autoFocus={!isEditForm}
          disabled={Boolean(contractorId)}
          {...form.getInputProps("email")}
          success={fieldValidationStatus.email}
          hasClearButton={!fieldValidationStatus.email}
          onClear={() => form.setFieldValue("email", "")}
        />
        <TextInput
          label="Mobile"
          minLength={10}
          maxLength={10}
          required
          autoFocus={!isEditForm}
          disabled={Boolean(contractorId)}
          {...form.getInputProps("mobile")}
          success={fieldValidationStatus.mobile}
          hasClearButton={!fieldValidationStatus.mobile}
          onClear={() => form.setFieldValue("mobile", "")}
        />
        <TextInput
          label="Contractor ID"
          required
          autoFocus={!isEditForm}
          disabled={Boolean(contractorId)}
          {...form.getInputProps("contractorId")}
          hasClearButton
          onClear={() => form.setFieldValue("contractorId", "")}
        />
        <RadioGroup
          label="Transaction Allowed"
          required
          {...form.getInputProps("isTransactionAllowed", {
            withRef: false,
            parser: (val) => val === "true",
          })}
        >
          <Radio label="Yes" value="true" />
          <Radio label="No" value="false" />
        </RadioGroup>
      </form>
    </Content>
  );
}

interface FormFeedbackProps {
  isEditForm: boolean;
  successMessage?: string;
  errorMessage?: string;
  onAddAnother?: () => void;
  onTryAgain?: () => void;
  onClose: () => void;
}

function FormFeedback({
  isEditForm,
  successMessage,
  errorMessage,
  onAddAnother,
  onTryAgain,
  onClose,
}: FormFeedbackProps) {
  const isSuccess = Boolean(successMessage);
  const isError = Boolean(errorMessage);

  if (!isSuccess && !isError) return null;

  const icon = isSuccess ? (
    <CheckCircleIcon
      variant="fill"
      color="var(--clr-info-success-default)"
      size="el"
    />
  ) : (
    <WarningIcon
      variant="fill"
      color="var(--clr-info-alert-default)"
      size="el"
    />
  );

  const headingText = isSuccess
    ? successMessage
    : `Contractor ${isEditForm ? "updation" : "addition"} failed`;

  const subText = isError ? (
    <p className="fs-14 fw-400 primary-color my-2">{errorMessage}</p>
  ) : null;

  const actionButton =
    isSuccess && !isEditForm ? (
      <Button
        size="lg"
        variant="primary"
        color="brand"
        leftSection={<PlusIcon size="md" />}
        onClick={onAddAnother}
      >
        Add Another
      </Button>
    ) : isError ? (
      <Button size="lg" variant="primary" color="brand" onClick={onTryAgain}>
        Try again
      </Button>
    ) : null;

  return (
    <Content
      header={() => <ContentHeader isEditForm={isEditForm} />}
      fixedHeaderFooter
    >
      <div className="d-flex align-items-center justify-content-center flex-column h-100">
        {icon}
        <h4 className="fs-20 fw-500 primary-color my-4 text-center">
          {headingText}
        </h4>
        {subText}
        {actionButton && <div className="my-3">{actionButton}</div>}
        <CloseButton onClose={onClose} />
      </div>
    </Content>
  );
}
